import { useAppStore } from '../../state/appStore';
import { useProgressStore } from '../../state/progressStore';
import { useT } from '../hooks';
import { BigButton, Panel, ScreenShell } from '../components';

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col items-stretch justify-between gap-2 py-3 sm:flex-row sm:items-center sm:gap-4">
      <div className="text-white/80">{label}</div>
      <div className="flex flex-wrap gap-2 sm:justify-end">{children}</div>
    </div>
  );
}

function Opt({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded-lg px-3 py-1.5 text-sm transition ${
        active ? 'bg-sky-500 font-semibold text-slate-950' : 'bg-white/10 hover:bg-white/20'
      }`}
    >
      {children}
    </button>
  );
}

export function SettingsScreen() {
  const t = useT();
  const { setScreen, lang, setLang, settings, updateSettings } = useAppStore();
  const resetAll = useProgressStore((s) => s.resetAll);

  return (
    <ScreenShell>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h2 className="min-w-0 flex-1 break-words text-2xl font-bold sm:text-3xl">{t('settings.title')}</h2>
        <BigButton className="shrink-0" variant="ghost" onClick={() => setScreen('menu')}>
          ← {t('settings.back')}
        </BigButton>
      </div>
      <Panel className="divide-y divide-white/10 px-5 py-2">
        <Row label={t('settings.language')}>
          <Opt active={lang === 'es'} onClick={() => setLang('es')}>
            Español
          </Opt>
          <Opt active={lang === 'en'} onClick={() => setLang('en')}>
            English
          </Opt>
        </Row>
        <Row label={t('settings.drivingSide')}>
          <Opt
            active={settings.drivingSide === 'right'}
            onClick={() => updateSettings({ drivingSide: 'right' })}
          >
            {t('settings.right')}
          </Opt>
          <Opt
            active={settings.drivingSide === 'left'}
            onClick={() => updateSettings({ drivingSide: 'left' })}
          >
            {t('settings.left')}
          </Opt>
        </Row>
        <Row label={t('settings.units')}>
          <Opt active={settings.units === 'kmh'} onClick={() => updateSettings({ units: 'kmh' })}>
            km/h
          </Opt>
          <Opt active={settings.units === 'mph'} onClick={() => updateSettings({ units: 'mph' })}>
            mph
          </Opt>
        </Row>
        <Row label={t('settings.audio')}>
          <Opt active={settings.audio} onClick={() => updateSettings({ audio: true })}>
            {t('settings.on')}
          </Opt>
          <Opt active={!settings.audio} onClick={() => updateSettings({ audio: false })}>
            {t('settings.off')}
          </Opt>
        </Row>
        <Row label={t('settings.camera')}>
          <Opt
            active={settings.camera === 'chase'}
            onClick={() => updateSettings({ camera: 'chase' })}
          >
            {t('settings.chase')}
          </Opt>
          <Opt
            active={settings.camera === 'cockpit'}
            onClick={() => updateSettings({ camera: 'cockpit' })}
          >
            {t('settings.cockpit')}
          </Opt>
        </Row>
      </Panel>
      <BigButton
        variant="danger"
        onClick={() => {
          if (window.confirm(t('settings.resetConfirm'))) void resetAll();
        }}
      >
        🗑️ {t('settings.reset')}
      </BigButton>
    </ScreenShell>
  );
}
